import type { AppDispatch } from '@redux/store'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as templatePagePartialActions from '@redux/modules/templatePagePartial'

import defaultFormState, { requiredFields } from '@models/templatePagePartial'
import type { TemplatePageModel, TemplatePageRequestOptions } from '@models/types'

import mergeHashQueryParamsWithRedirect from '@functions/mergeHashQueryParamsWithRedirect'

export const generateUrls = (
  options: { includeRedirect?: boolean } = {},
) => {
  const { includeRedirect } = options

  return {
    archivedTemplatePagePartialsIndexUrl: mergeHashQueryParamsWithRedirect({
      hash: '#/templatePagePartials?hidden=true',
      includeRedirect,
    }),
    templatePagePartialsIndexUrl: mergeHashQueryParamsWithRedirect({
      hash: '#/templatePagePartials?hidden=false',
      includeRedirect,
    }),
  }
}

type CreateTemplatePagePartialParams = {
  dispatch: AppDispatch,
  requestOptions?: TemplatePageRequestOptions,
  templatePagePartialParams: TemplatePageModel,
}
const createTemplatePagePartial = (params: CreateTemplatePagePartialParams) => {
  const { templatePagePartialParams, dispatch, requestOptions } = params
  const { createTemplatePagePartial: createFn } = templatePagePartialActions

  return dispatch(createFn(templatePagePartialParams, requestOptions))
}

type DuplicateTemplatePagePartialParams = {
  dispatch: AppDispatch,
  templatePagePartial: TemplatePageModel,
}

const duplicateTemplatePagePartial = (params: DuplicateTemplatePagePartialParams) => {
  const { templatePagePartial, dispatch } = params
  const { duplicateTemplatePagePartial: duplicateFn } = templatePagePartialActions

  return dispatch(duplicateFn(templatePagePartial))
}

type ToggleTemplatePagePartialVisbilityParams = {
  dispatch: AppDispatch,
  templatePagePartial: TemplatePageModel,
}

const toggleTemplatePagePartialVisibility = (params: ToggleTemplatePagePartialVisbilityParams) => {
  const { templatePagePartial, dispatch } = params
  const { toggleTemplatePagePartialVisibility: toggleVisibilityFn } = templatePagePartialActions

  return dispatch(toggleVisibilityFn(templatePagePartial))
}

export type ShowCreateTemplatePartialModalPayload = {
  templatePagePartial: TemplatePageModel,
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTemplatePagePartialForm(
  templatePagePartial: Partial<TemplatePageModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const templatePagePartialForm = useForm(
    defaultFormState,
    { entity: templatePagePartial, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [templatePagePartial.id, templatePagePartial.cache_key],
  )

  return {
    ...templatePagePartialForm,
  }
}

// Return related entities
export const useRelations = (entity: Partial<TemplatePageModel> = {}) => {
  const { brand_id } = entity

  const { brands } = useSelector(reduxState => reduxState.entities)

  const brand = brand_id && brands[brand_id] ? brands[brand_id] : {}

  return {
    brand,
  }
}

function useTemplatePagePartial(initEntity: Partial<TemplatePageModel> = {}) {
  const { entity: templatePagePartial } = useLatestEntity(initEntity, 'templatePagePartials')

  const dispatch = useDispatch()

  const { brand } = useRelations(templatePagePartial)

  return {
    brand,
    callbacks: {
      createTemplatePagePartial: (templatePagePartialParams: TemplatePageModel) => createTemplatePagePartial(
        { dispatch, templatePagePartialParams },
      ),
      duplicateTemplatePagePartial: () => duplicateTemplatePagePartial({ dispatch, templatePagePartial }),
      toggleTemplatePagePartialVisibility: () => toggleTemplatePagePartialVisibility({ dispatch, templatePagePartial }),
    },
    templatePagePartial,
    urls: generateUrls(),
  }
}

export default useTemplatePagePartial
